import type {ProjectPickerRelayProject as ProjectPickerProject} from '@github-ui/item-picker-project-relay'

import type {IssueCreatePayload} from './model'
import {instanceOfIssueFormData} from './model'

export type ProjectInitializationOptions = {
  template?: IssueCreatePayload | null
  projects?: ProjectPickerProject[]
  isParentInheritance?: boolean
}

/**
 * Extracts projects from an issue template.
 */
function getProjectsFromTemplate(template: IssueCreatePayload): ProjectPickerProject[] {
  let initialProjects: ProjectPickerProject[] = []
  if (template?.data && instanceOfIssueFormData(template?.data)) {
    initialProjects = template.data.projects?.edges?.flatMap(e => (e?.node ? [e.node] : [])) || []
  }
  return initialProjects
}

/**
 * Utility function for determining initial projects for issue creation based on template and parent inheritance.
 * This follows the same pattern as other metadata (labels, assignees, etc.) in the codebase.
 *
 * Priority logic:
 * 1. Explicit non-parent projects (URL params, Memex, etc.) - highest precedence
 * 2. Template projects - override parent inheritance
 * 3. Parent projects (inheritance) - used when no template projects
 * 4. Empty array (final fallback)
 */
export function getInitialProjects(options: ProjectInitializationOptions): ProjectPickerProject[] {
  const {template, projects = [], isParentInheritance = false} = options

  // For non-parent contexts (URL params, Memex, etc.), explicit projects take precedence
  if (!isParentInheritance && projects.length > 0) {
    return projects
  }

  // Template projects take precedence over parent inheritance
  const templateProjects = template ? getProjectsFromTemplate(template) : []
  if (templateProjects.length > 0) {
    return templateProjects
  }

  // Fall back to parent projects (when isParentInheritance is true) or empty array
  return projects
}
